import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-28 pb-24 sm:pb-32">
      {/* Minecraft block grid backdrop */}
      <div className="absolute inset-0 bg-blocks pointer-events-none" />
      {/* enchant glow blob */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-kquest-500/25 blur-[140px] pointer-events-none" />
      {/* drifting "floating block" art (CSS) */}
      <div className="absolute right-12 top-32 hidden lg:block animate-float pointer-events-none">
        <div className="w-16 h-16 block-purple rotate-12" />
      </div>
      <div className="absolute left-16 bottom-20 hidden lg:block animate-float pointer-events-none" style={{ animationDelay: '2s' }}>
        <div className="w-12 h-12 block-gold -rotate-6" />
      </div>
      <div className="absolute right-1/4 bottom-32 hidden lg:block animate-float pointer-events-none" style={{ animationDelay: '4s' }}>
        <div className="w-10 h-10 block-xp rotate-3" />
      </div>

      <div className="relative container-narrow px-6 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-8"
        >
          <div className="pill-pixel bg-kquest-900 border-kquest-400 text-kquest-200">
            <Sparkles className="w-3.5 h-3.5 text-gold-400" />
            <span>{t('hero.tagline')}</span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="font-pixel text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight text-center leading-[1.05]"
        >
          <span className="block text-kquest-50">{t('hero.title_pre')}</span>
          <span className="block text-gradient-enchant mt-2 animate-glitch">
            {t('hero.title_main')}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 text-lg sm:text-xl text-kquest-200 text-center max-w-3xl mx-auto leading-relaxed"
        >
          {t('hero.subtitle')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/submit" className="btn-gold text-base !px-8 !py-4">
            <Trophy className="w-5 h-5" />
            {t('hero.cta_primary')}
          </Link>
          <Link to="/quests" className="btn-purple text-base !px-8 !py-4">
            {t('hero.cta_secondary')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-20 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto"
        >
          {[
            { value: '12',   label: t('hero.stats_active') },
            { value: '47',   label: t('hero.stats_completed') },
            { value: '1.4K', label: t('hero.stats_voters') },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-press text-2xl sm:text-3xl lg:text-4xl text-gradient-enchant">
                {s.value}
              </div>
              <div className="mt-3 text-xs sm:text-sm text-kquest-300 font-pixel uppercase tracking-wider">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
